import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const API = process.env.REACT_APP_API || '/api';

// حفظ الجلسة على المتصفح اختياري: localStorage يبقى بعد الإغلاق،
// وsessionStorage ينتهي بإغلاق التبويب.
const readToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const writeToken = (value, remember) => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  (remember ? localStorage : sessionStorage).setItem('token', value);
};

// جلسة الموظّف تُحفظ أثناء معاينة بوابة عميل حتى تعود كما كانت بعد الخروج منها
const ACTOR_KEY = 'impersonator';
const readActor = () => {
  try { return JSON.parse(sessionStorage.getItem(ACTOR_KEY)); } catch { return null; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readToken());
  const [impersonator, setImpersonator] = useState(readActor);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(u => { setUser(u); setLoading(false); })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password, remember = true) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    writeToken(data.token, remember);
    if (remember) localStorage.setItem('lastEmail', email);
    else localStorage.removeItem('lastEmail');
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    // إبطال التوكن على الخادم أولاً — كان يُمسح محلياً فقط فتتراكم
    // توكنات Sanctum صالحة إلى الأبد
    const actor = readActor();
    const revoke = (t) => fetch(`${API}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}` },
    });
    try {
      await revoke(token);
      // الخروج من داخل المعاينة يُبطل توكن الموظّف أيضاً حتى لا يبقى بلا جلسة
      if (actor) await revoke(actor.token);
    } catch { /* الخروج المحلّي يتمّ في كل الأحوال */ }
    sessionStorage.removeItem(ACTOR_KEY);
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setImpersonator(null);
    setToken(null);
    setUser(null);
  };

  /**
   * فتح بوابة عميل بحساب الموظّف الحالي. توكن الموظّف يُنحّى جانباً ليُستعاد
   * عند إنهاء المعاينة، والجلسة الجارية تصبح جلسة العميل بالكامل.
   */
  const impersonateClient = async (clientId) => {
    const data = await api(`/clients/${clientId}/impersonate`, { method: 'POST' });
    const actor = { token, remember: !!localStorage.getItem('token'), name: user?.name };
    sessionStorage.setItem(ACTOR_KEY, JSON.stringify(actor));
    writeToken(data.token, false);
    setImpersonator(actor);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const stopImpersonating = async () => {
    const actor = readActor();
    if (!actor) return;
    try {
      await fetch(`${API}/auth/impersonation/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* استعادة جلسة الموظّف تتمّ في كل الأحوال */ }
    sessionStorage.removeItem(ACTOR_KEY);
    writeToken(actor.token, actor.remember);
    setImpersonator(null);
    setUser(null);
    setLoading(true);
    setToken(actor.token);
  };

  const api = async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json', ...opts.headers }
    });
    if (res.status === 401) { logout(); throw new Error('جلسة منتهية'); }
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      // أخطاء التحقّق في Laravel تأتي في data.errors، والرسالة العامة في data.message
      const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
      throw new Error(data?.error || firstError || data?.message || 'حدث خطأ');
    }
    return data;
  };

  // XHR-based so callers can observe real upload progress via onProgress(0–100).
  const apiUpload = (path, formData, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}${path}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Accept', 'application/json');
      if (typeof onProgress === 'function' && xhr.upload) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }
      xhr.onload = () => {
        let data = {};
        try { data = JSON.parse(xhr.responseText); } catch {}
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else if (xhr.status === 401) { logout(); reject(new Error('جلسة منتهية')); }
        else {
          const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
          reject(new Error(data.error || firstError || data?.message || 'فشل الرفع'));
        }
      };
      xhr.onerror = () => reject(new Error('فشل الرفع'));
      xhr.send(formData);
    });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, api, apiUpload, impersonator, impersonateClient, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}
